import type {DiffEntry} from '../../../conversation-types'
import {IconButton} from '@primer/react'
import {StaticUnifiedDiffPreview} from '../StaticUnifiedDiffPreview'
import styles from './AutomatedReviewDiffEntry.module.css'
import {useState} from 'react'
import {clsx} from 'clsx'
import {ChevronDownIcon, ChevronRightIcon} from '@primer/octicons-react'

export function AutomatedReviewDiffEntry({
  diffEntry,
  initialCollapsed = false,
  pathInLabel = false,
  wrapPath = false,
  exposeExpandedState = false,
}: {
  diffEntry: DiffEntry
  initialCollapsed?: boolean
  /**
   * Include the file path in the toggle's accessible name so screen-reader users can
   * tell which file each toggle controls when several render together (e.g. the batch
   * panel). Off by default to leave single-suggestion consumers unchanged.
   */
  pathInLabel?: boolean
  /**
   * Wrap the path in a span so a long path wraps instead of being clipped by an
   * `overflow-hidden` ancestor in a narrow container. Off by default.
   */
  wrapPath?: boolean
  /**
   * Expose `aria-expanded` on the toggle so assistive tech can query the diff's
   * open/closed state. Opt-in (like the other panel props) so the flag-gated batch
   * panel gets it without changing the DOM for existing consumers when the flag is off.
   */
  exposeExpandedState?: boolean
}) {
  const [isCollapsed, setIsCollapsed] = useState(initialCollapsed)
  const onToggleCollapsed = () => setIsCollapsed(prev => !prev)

  const toggleVerb = isCollapsed ? 'Open' : 'Close'
  const toggleLabel = pathInLabel
    ? `${toggleVerb} review comment for ${diffEntry.path}`
    : `${toggleVerb} review comment`

  return (
    <div key={diffEntry.path} className="d-flex flex-column overflow-hidden">
      <div
        className={clsx(
          'd-flex flex-items-center tmp-px-3 py-2 text-small gap-2 text-mono',
          styles.diffEntryHeader,
          isCollapsed && 'rounded-2',
        )}
      >
        <IconButton
          aria-label={toggleLabel}
          aria-expanded={exposeExpandedState ? !isCollapsed : undefined}
          icon={isCollapsed ? ChevronRightIcon : ChevronDownIcon}
          size="small"
          variant="invisible"
          onClick={onToggleCollapsed}
        />
        {wrapPath ? <span className={styles.path}>{diffEntry.path}</span> : diffEntry.path}
      </div>
      {!isCollapsed && (
        <StaticUnifiedDiffPreview
          // TODO(@aliceclv): pass tabSize dynamically
          tabSize={4}
          className={clsx('m-0 border-bottom borderColor-default overflow-hidden', styles.automatedReviewDiff)}
          diffTableClassName="borderColor-default"
          diffLines={diffEntry.diffLines}
        />
      )}
    </div>
  )
}
